import axios from "axios";
import { NotImplementedError } from "../../errors";
import { PromiseHandler } from "../../handlers";
import { parseIdFilter } from "../../handlers/filter/utils";
import { generateCreativesRequestToService, generateJobResponseFromService, generateJobStatusResponseFromService } from "../../models/aiCreative";
import { AiCreativeGenerateCreativesFailed, AiCreativeGenerateJobStatusFailed } from "./errors";
/**
 * Triggers services/creative's async generate-jobs pipeline and looks up a
 * job's status by id.
 *
 * `generate` stays a bespoke method — it enqueues a job and returns a
 * different-shaped payload than a status lookup, with no id to key off of
 * until the POST resolves, so it doesn't fit `save`. `getStatus`/`find` is a
 * genuine id-keyed lookup, though, so it's wired through the standard
 * `PromiseHandler` surface — {@link BrandKits} follows the same split for
 * `analyze` vs. `find`.
 */
export default class GenerateJobs extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "aiCreative.generateJobs");
    }
    async generate(request, idempotencyKey) {
        try {
            const res = await axios.post(`${this.sdk.urls.creativeBaseUrl}/api/v1/creative/generate-jobs`, { ...generateCreativesRequestToService(request), idempotency_key: idempotencyKey });
            return generateJobResponseFromService(res.data);
        }
        catch (err) {
            throw new AiCreativeGenerateCreativesFailed(err);
        }
    }
    async findItems(filters = {}) {
        const [id] = parseIdFilter(filters);
        if (!id) {
            throw new NotImplementedError("find without a job id — generate-job status is fetched one at a time");
        }
        const brand = filters.where?.find((clause) => clause.field === "brand")?.value;
        return [await this.getStatus(String(id), brand)];
    }
    async getStatus(jobId, brand) {
        try {
            const res = await axios.get(`${this.sdk.urls.creativeBaseUrl}/api/v1/creative/generate-jobs/status`, { params: { job_id: jobId, brand } });
            return generateJobStatusResponseFromService(res.data);
        }
        catch (err) {
            throw new AiCreativeGenerateJobStatusFailed(err);
        }
    }
    /**
     * @deprecated
     * `save` is not supported — a job is created via {@link generate}, not by
     * saving a status object back.
     */
    saveItem() {
        throw new NotImplementedError("save");
    }
    /**
     * @deprecated
     * `delete` is not supported — generate-jobs are not removable through this API.
     */
    deleteItem() {
        throw new NotImplementedError("delete");
    }
    /**
     * @deprecated
     * `make` is not supported — a job only exists once {@link generate} has
     * enqueued it.
     */
    async make() {
        throw new NotImplementedError("make");
    }
}
//# sourceMappingURL=generateJobs.js.map